import { createApp } from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import vuetify from "./plugins/vuetify";
import { loadFonts } from "./plugins/webfontloader";
import VueGtag from "vue-gtag-next";
import { createPinia } from "pinia";
import { vMaska } from "maska/vue";
import { useConfigStore } from "@/stores/config";
import { useConnectivityStore } from "@/stores/connectivity";
import auth from "@/auth";
import { useUserStore } from "@/stores/userStore";
import axios from "axios";
import { useStructureIDBStore } from "@/stores/structureIDB";
import { useAdminStore } from "@/stores/admin";
import { ENV_CONFIG_PROPERTY } from "@/constants/EnvConfigProperties";
import axiosRetry from "axios-retry";
import { useUserPermissionStore } from "@/stores/userPermission";
import { useSnackbarStore } from "@/stores/snackbar";
import { SNACKBAR_MESSAGE_TYPES, SNACKBAR_MARKER } from "@/constants/GlobalSnackbar.js";
import { HTTP_STATUS_CODE } from "@/constants/CommonWebCodes";

loadFonts();

const app = createApp(App);
app.use(createPinia());
const userStore = useUserStore();
const connectivity = useConnectivityStore();
const userPermissionStore = useUserPermissionStore();
const snackbarStore = useSnackbarStore();
function init() {
  app.use(vuetify).use(VueGtag, {
    config: { id: "G-4P7XKRBH5M" },
    useDebugger: import.meta.env.DEV,
  });
  app.directive("maska", vMaska);
}

const loadConfigs = async () => {
  const isPersisted = await navigator?.storage?.persisted();
  if (!isPersisted) {
    await navigator?.storage?.persist();
  }
  const user = await auth.getUser();
  const configStore = useConfigStore();

  Promise.all([
    configStore.loadEnvProperties(user),
    configStore.loadUsers(user),
    configStore.loadNbisBridgeInspectors(user),
    configStore.loadNbisNSTMBridgeInspectors(user),
    configStore.loadNbisTunnelInspectors(user),
    configStore.loadInspectors(user),
    configStore.loadUnderwaterDivers(user),
    configStore.loadReferenceData(user),
    configStore.loadDistricts(user),
    configStore.loadCounties(user),
    configStore.loadMunicipalities(user),
    configStore.loadScourCriticalBridgeIndicators(user),
    configStore.loadElementDefinitions(user),
    configStore.loadDepartmentStructureTypeDescriptions(user),
    configStore.loadElementChildren(user),
    configStore.loadElementStateDefinitions(user),
    configStore.loadDocTypes(user),
    configStore.loadFlexActions(user),
    configStore.loadBridgeActions(user),
    configStore.loadCodeFilters(user),
    configStore.loadOrganizations(user),
    configStore.loadSPNOrganizations(user),
    configStore.loadInspectionReportSection(user),
    configStore.loadInspectorId(user),
    configStore.loadCommentsFilters(user),
    configStore.loadServiceTypes(user),
  ]).then(() => {
    app.use(router).mount("#app");
  });
};

const addInterceptor = () => {
  axios.interceptors.request.use(async (config) => {
    const user = await auth.getUser();
    config.headers.Authorization = `Bearer ${user?.id_token}`;
    config.headers["bms-groups"] = user?.profile.groups;
    config.headers["ha-user-id"] = user?.profile.HAUserID;
    config.headers["bms-user-id"] = user?.profile.sub;
    config.headers["bms-bp-id"] = user?.profile.HAFirmID;
    config.headers["bms-district"] = user?.profile.WorkUnitCode?.slice(0, 2);
    config.headers["bms-email"] = user?.profile.email;
    config.headers["bms-last-name"] = user?.profile.family_name;
    config.headers["bms-first-name"] = user?.profile.given_name;
    config.headers["bms-domino-full-name"] = user?.profile.DominoFullName;
    return config;
  });
};

const onRetry = async (_, error) => {
  await new Promise((resolve, reject) => {
    if (error.code == "ERR_NETWORK") {
      reject(new Error("Skip retries when no internet connection"));
    } else {
      resolve();
    }
  });
};

const addRetryConfig = () => {
  axiosRetry(axios, {
    retries: 3,
    retryDelay: (...arg) => axiosRetry.exponentialDelay(...arg, 1000),
    onRetry: onRetry,
  });
};

const storePass = async () => {
  const adminStore = useAdminStore();
  const configStore = useConfigStore();

  const userId = userStore.getUserSub();

  const pouchUser = await adminStore.getUserInfo(userId);
  if (pouchUser) {
    localStorage.setItem("hashedPass", pouchUser?.passwordHash);
  } else {
    const defaultHash = configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.OFFLINE_DEFAULT_PASS);
    //get default password
    localStorage.setItem("hashedPass", defaultHash);
    adminStore.saveUser({ userId, passwordHash: defaultHash });
  }
};

async function authenticateUser() {
  init();
  await connectivity.getServiceStatus();
  if (connectivity.getisOnline) {
    await auth.setToken();
    if (!userStore.isAuthenticated) {
      await auth.login();
    }
    addInterceptor();
    addRetryConfig();
  } else if (userStore.isAuthenticated) {
    router.push({ name: "ViewWorkList" });
  } else {
    router.push({ name: "OfflineLogin" });
  }
}

await authenticateUser();
await loadConfigs();
if (connectivity.getisOnline) {
  await userStore.addUser();
  await storePass();
  const structureIDBStore = useStructureIDBStore();
  await structureIDBStore.initDB(false);
  await structureIDBStore.saveUserInfo(userStore?.loggedInUser);
  await userPermissionStore.getUserAppPermission();
}
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status == HTTP_STATUS_CODE.CONFLICT) {
      snackbarStore.showMessage({
        displayText: `Record cannot be deleted. Child record exists.`,
        timeout: 10000,
        messageType: SNACKBAR_MESSAGE_TYPES.ERROR,
        marker: SNACKBAR_MARKER.GLOBAL_FOREIGN_KEY_ERROR,
      });
    }
    throw error;
  },
);
